import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Input, Select, Space } from 'antd'
import type {
    IJourneySubscriberFcapRequest,
    SubscriberFcapType,
    SubscriberFcapWindow,
} from '@pushly/models/lib/structs/journeys/requests/request-interfaces'

type EntryCapSelection = SubscriberFcapType | 'no-fcap'

export interface ISubscriberEntryCapSectionProps {
    disabled: boolean
    cap: IJourneySubscriberFcapRequest | undefined
    onChange: (next: IJourneySubscriberFcapRequest | undefined) => void
}

const SubscriberEntryCapSectionCmp: React.FC<ISubscriberEntryCapSectionProps> = ({ disabled, cap, onChange }) => {
    const entryCapType: EntryCapSelection = cap?.type ?? 'no-fcap'
    const showOncePerCriteria = entryCapType === 'once_per_criteria'
    const entryCapFrequency = cap?.criteria?.frequency
    const entryCapWindow = cap?.criteria?.window

    const handleTypeChange = (value: EntryCapSelection) => {
        if (disabled) return
        if (value === 'no-fcap') {
            onChange(undefined)
            return
        }

        const nextCap: IJourneySubscriberFcapRequest = { ...cap, type: value }

        if (value === 'once_per_criteria' && !cap) {
            nextCap.criteria = { frequency: 1, window: 'days' }
        }

        onChange(nextCap)
    }

    const handleFreqChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return
        const raw = e?.target?.value
        const parsed = raw === '' ? undefined : Number.parseInt(raw, 10)
        const freq = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined
        onChange({
            type: cap?.type ?? 'once_per_criteria',
            criteria: {
                frequency: freq as number,
                window: cap?.criteria?.window ?? 'days',
            },
        })
    }

    const handleWindowChange = (value: SubscriberFcapWindow) => {
        if (disabled) return
        onChange({
            type: cap?.type ?? 'once_per_criteria',
            criteria: {
                frequency: cap?.criteria?.frequency ?? 1,
                window: value,
            },
        })
    }

    return (
        <Well variant="ghost" title="Entry Frequency Cap" footerMode="hidden" padding="none" className="entry-cap-well">
            <Space align="center">
                <span>Subscribers not currently in this journey</span>
                <Select
                    size="small"
                    disabled={disabled}
                    value={entryCapType}
                    onChange={handleTypeChange}
                    options={[
                        { value: 'no-fcap', label: 'may enter this journey every time it runs' },
                        { value: 'once', label: 'may only enter this journey once in their lifetime' },
                        { value: 'once_per_criteria', label: 'may only enter this journey once ever' },
                    ]}
                    style={{ width: 330 }}
                />
                {showOncePerCriteria && (
                    <Space.Compact size="small" style={{ width: 150 }}>
                        <Input
                            className="rcte-fcap-frequency"
                            size="small"
                            disabled={disabled}
                            defaultValue={entryCapFrequency}
                            value={entryCapFrequency}
                            onChange={handleFreqChange}
                        />
                        <Select
                            size="small"
                            className="rcte-fcap-window-select"
                            disabled={disabled}
                            defaultValue={entryCapWindow}
                            value={entryCapWindow}
                            onChange={handleWindowChange}
                            options={[
                                { value: 'days', label: 'day(s)' },
                                { value: 'months', label: 'month(s)' },
                            ]}
                        />
                    </Space.Compact>
                )}
            </Space>
        </Well>
    )
}

export default React.memo(SubscriberEntryCapSectionCmp)
